/**
 * Low-poly wedge glider, authored in code (spec Section 7). Unlit emissive —
 * normals are irrelevant, silhouette is everything.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { makeFlatMaterial } from "./scene";

export function createShipMesh(scene: Scene): Mesh {
  const mesh = new Mesh("ship", scene);

  //        3 (peak)
  //       /|\
  //  1---0---2      0 = nose (forward +z), 1/2 = tail corners, 4 = tail center low
  const positions = [
    0.0, 0.12, 0.95, // 0 nose
    -0.55, 0.0, -0.65, // 1 tail left
    0.55, 0.0, -0.65, // 2 tail right
    0.0, 0.42, -0.5, // 3 peak
    0.0, -0.08, -0.6, // 4 keel
  ];
  const indices = [
    0, 3, 1, // upper left
    0, 2, 3, // upper right
    1, 3, 2, // tail upper
    0, 1, 4, // keel left
    0, 4, 2, // keel right
    1, 2, 4, // tail lower
  ];
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);

  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  data.applyToMesh(mesh);

  mesh.material = makeFlatMaterial(scene, "mat-ship", "#9BEBF7", 1.0);
  mesh.isPickable = false;
  return mesh;
}

/**
 * Engine flame (R2-M4 VFX-1): a warm emissive spike off the tail whose length
 * follows effRate — idle stub at 0.6, roaring tongue at 1.6. Parented to the
 * ship; scaled in place each frame, never reallocated.
 */
export function createEngineFlame(scene: Scene, ship: Mesh): Mesh {
  const flame = new Mesh("engine-flame", scene);
  // slim octahedron-ish spike pointing −z (behind the tail)
  const positions = [
    0, 0.08, -0.6, // base center (at the keel)
    -0.16, 0.08, -0.72,
    0.16, 0.08, -0.72,
    0, 0.2, -0.72,
    0, -0.02, -0.72,
    0, 0.08, -1.6, // tip — scaling z stretches this
  ];
  const indices = [
    0, 1, 3, 0, 3, 2, 0, 2, 4, 0, 4, 1,
    1, 5, 3, 3, 5, 2, 2, 5, 4, 4, 5, 1,
  ];
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  data.applyToMesh(flame);
  const mat = makeFlatMaterial(scene, "mat-flame", "#FFB35C", 1.3);
  mat.alpha = 0.9;
  flame.material = mat;
  flame.isPickable = false;
  flame.parent = ship;
  return flame;
}

/** PB-ghost ship: same wedge at 35 % alpha, additive glow, never collidable
 *  (spec 6.6 — collision is impossible by architecture: ghosts run in their
 *  own Sim and never touch the player's). */
export function createGhostMesh(scene: Scene): Mesh {
  const mesh = createShipMesh(scene);
  mesh.name = "ghost-ship";
  const mat = mesh.material as import("@babylonjs/core/Materials/standardMaterial").StandardMaterial;
  mat.alpha = 0.35;
  mat.alphaMode = 1; // additive
  mesh.setEnabled(false);
  return mesh;
}
